import compact from 'lodash/compact';
import sumBy from 'lodash/sumBy';

import { ZigzagEvent } from '@common/log-manager';
import { airbridgeTracker } from '@common/marketing-tracker';

import { ManagedCategoryList } from '../../type/common.type';
import { FbPixelEventEnum, trackFbPixel } from './tracking/facebook-pixel';
import { trackGa4 } from './tracking/ga4';
import { trackKakaoPixel } from './tracking/kakao-pixel';
import { AddToCartParams, PageViewOptions, PageViewParams, ShareEventParams } from './useExternalToolLog.type';

export const useExternalToolLog = () => {
  const currency = 'KRW';

  const pageViewEvent = (params: PageViewParams, { enabledAirbridge = true }: PageViewOptions = {}) => {
    if (currency === 'KRW') {
      if (enabledAirbridge) {
        const { mainCategory, combinedCategoryId, combinedCategoryName } = generateCategoriesForAirbridge(
          params.managedCategoryList,
        );

        airbridgeTracker.events.send('airbridge.ecommerce.product.viewed', {
          value: params.price,
          label: mainCategory?.value,
          semanticAttributes: {
            products: [
              {
                productID: params.product_id,
                name: params.product_name,
                categoryID: combinedCategoryId,
                categoryName: combinedCategoryName,
                brandID: params.shop_id,
                brandName: params.shop_name,
                price: params.price,
                currency,
              },
            ],
          },
        });
      }

      trackGa4('view_item', {
        currency,
        value: params?.price,
        items: [
          {
            item_id: params?.product_id,
            item_name: params?.product_name,
            price: params?.price,
            quantity: params?.quantity,
          },
        ],
      });
      trackFbPixel(FbPixelEventEnum.ViewContent);
      trackKakaoPixel('viewContent', { id: params?.product_id });

      ZigzagEvent.logAppsFlyer('af_content_view', {
        af_content_id: params?.product_id,
        af_content_type: params?.category,
        af_currency: currency,
        af_price: params?.price,
        shop_id: params.shop_id,
      });

      ZigzagEvent.logFacebook('fb_mobile_content_view', {
        fb_content_id: params?.product_id,
        fb_content_type: params.category,
        fb_currency: currency,
        _valueToSum: params?.price,
        shop_id: params?.shop_id,
      });

      ZigzagEvent.logFirebase('view_item', {
        item_id: params?.product_id,
        item_category: params.category,
        currency,
        value: params?.price,
        shop_id: params?.shop_id,
      });
    }
  };

  const addToCartEvent = (params: AddToCartParams) => {
    if (currency === 'KRW') {
      const { mainCategory, combinedCategoryId, combinedCategoryName } = generateCategoriesForAirbridge(
        params.managedCategoryList,
      );
      airbridgeTracker.events.send('airbridge.ecommerce.product.addedToCart', {
        label: mainCategory?.value,
        semanticAttributes: {
          totalValue: sumBy(params.items, ({ price, quantity }) => price * quantity),
          currency,
          products: params.items.map((item) => ({
            productID: item.item_id,
            name: item.item_name,
            categoryID: combinedCategoryId,
            categoryName: combinedCategoryName,
            brandID: params.shop_name,
            currency,
            price: item.price,
            quantity: item.quantity,
          })),
        },
      });

      trackGa4('add_to_cart', {
        currency,
        value: params?.price,
        items: params?.items,
      });
      trackFbPixel(FbPixelEventEnum.AddToCart);
      trackKakaoPixel('addToCart', { id: params?.product_id });

      ZigzagEvent.logAppsFlyer('af_add_to_cart', {
        af_content_id: params?.product_id,
        af_content_type: params?.category,
        af_currency: currency,
        af_price: params?.price,
        shop_id: params?.shop_id,
      });
      ZigzagEvent.logFacebook('fb_mobile_add_to_cart', {
        fb_content: params?.product_id,
        fb_content_type: 'product',
        fb_currency: currency,
        _valueToSum: params?.price,
        shop_id: params?.shop_id,
      });
      ZigzagEvent.logFirebase('add_to_cart', {
        item_id: params?.product_id,
        item_category: params?.category,
        currency,
        value: params?.price,
        shop_id: params?.shop_id,
      });
    }
  };

  const shareEvent = (params: ShareEventParams) => {
    if (currency === 'KRW') {
      trackGa4('share', {
        /**
         * link | kakao | facebook
         */
        method: params?.method,
        /**
         * product | promotion
         */
        content_type: params?.content_type,
        item_id: params?.product_id,
      });
    }
  };

  return {
    pageViewEvent,
    addToCartEvent,
    shareEvent,
  };
};

const generateCategoriesForAirbridge = (categories: (ManagedCategoryList | null)[] | null) => {
  if (!categories) return {};

  const [mainCategory] = categories;
  const usedCategories = compact(categories);
  const combinedCategoryId = usedCategories.map(({ category_id }) => category_id).join(', ');
  const combinedCategoryName = usedCategories.map(({ value }) => value).join(', ');

  return {
    mainCategory,
    combinedCategoryId,
    combinedCategoryName,
  };
};
